import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifButton,
  MotifTheme,
  MotifCheckbox,
  MotifTruncate,
  MotifToast,
  MotifIcon,
} from "@ey-xd/motif-react";
import "./UpdateOrderModal.scss";
import { AppDispatch, RootState } from "../../../../store";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { fetchSkuListData } from "../../../../store/slices/CTSkuListSlice";
import Loader from "../../../../components/Loader";
import {
  resetCheckoutSate,
  ResponseDetails,
  SKU,
  updateOrderBundleList,
} from "../../../../store/slices/CheckoutSlice";
import { ViewDetails } from "../ViewDetails/ViewDetails";
import { OrderUpdateObj, SkuDetailsType, ViewDetailType } from "../../type";
import { getSkuListFromOrderBundle } from "../../CheckoutHelper";
import NumericInput from "../../../../components/NumericInput/NumericInput";
import {
  MAX_QUANTITY,
  MAX_DURATION_MONTHS,
  MAX_DURATION_WEEKS,
  RETIRED_SKU_IN_CHECKOUT_MESSAGE,
} from "../../../../../src/Constant";
import CheckBoxToolTip from "../../../ProductDetail/CheckBoxToolTip";
import { COST_INTERVAL_CODE } from "../../../ProductDetail/Constants";
import { IconoirWarningCircle } from "@ey-xd/motif-icon";

export default function UpdateOrderModal({
  show,
  setShow,
  onSave,
  productId = "",
  productName,
  checkOutListState,
  assetAdvice,
  isChangedActive,
  setIsChangedActive,
  setShowEngagementException,
  retiredSkuList,
  onOrderUpdated,
}: {
  show: boolean;
  setShow: (isVisible: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSave: (updatedComment: any) => void;
  productId: string;
  productName?: string;
  checkOutListState: ResponseDetails | null | undefined;
  setCheckOutListState: React.Dispatch<
    React.SetStateAction<ResponseDetails | null>
  >;
  assetAdvice?: null | JSX.Element | undefined | string;
  isChangedActive: boolean;
  setIsChangedActive: React.Dispatch<React.SetStateAction<boolean>>;
  setShowEngagementException: React.Dispatch<React.SetStateAction<boolean>>;
  retiredSkuList: string[];

  onOrderUpdated: () => void;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const isSkuListLoading = useSelector(
    (state: RootState) => state.CTSkuListSlice.isLoading,
  );
  const skuListData = useSelector(
    (state: RootState) => state.CTSkuListSlice.data,
  );
  const errorSkuList = useSelector(
    (state: RootState) => state.CTSkuListSlice.error,
  );

  let filterSelectedSkuList: SKU[] = [];
  if (checkOutListState && checkOutListState.Details.length > 0) {
    const filteredSkuList = checkOutListState.Details.filter(
      (item: any) => item.ProductId === productId,
    );
    if (filteredSkuList && filteredSkuList.length > 0) {
      filterSelectedSkuList = filteredSkuList[0].SkuDetails;
    }
  }

  const unFilterSelectedSkuList: any = [];
  if (checkOutListState && checkOutListState.Details.length > 0) {
    const unFilteredSkuList = checkOutListState.Details.filter(
      (item: any) => item.ProductId !== productId,
    );
    unFilteredSkuList.forEach((item: any) => {
      if (item.SkuDetails && item.SkuDetails.length > 0) {
        item.SkuDetails.forEach((sku: any) => {
          const OrderSku = {
            SkuId: sku.SkuId,
            ParentSkuId: "",
            Quantity: Number(sku.OrderQuantity),
            Intervals: Number(sku.OrderInterval),
          };
          unFilterSelectedSkuList.push(OrderSku);
          sku.SkuRelation?.forEach((relation: any) => {
            const OrderSku = {
              SkuId: relation.SkuId,
              ParentSkuId: sku.SkuId,
              Quantity: Number(relation.OrderQuantity),
              Intervals: Number(relation.OrderInterval),
            };
            unFilterSelectedSkuList.push(OrderSku);
          });
        });
      }
    });
  }

  const defaultSelectedSkuList = filterSelectedSkuList.map((sku: any) => ({
    ...sku,
    IsSelected: true,
    Quantity: sku.OrderQuantity || (sku.CostIntervalCd === "onetime" ? 1 : ""),
    Duration: sku.OrderInterval === "onetime" ? 1 : sku.OrderInterval,
    TotalCost: 0,
    SkuRelation: sku?.SkuRelation?.map((relation: any) => ({
      ...relation,
      Quantity:
        relation.OrderQuantity ||
        (relation.CostIntervalCd === "onetime" ? 1 : ""),
      Duration:
        relation.OrderInterval === "onetime" ? 1 : relation.OrderInterval,
      TotalCost: 0,
    })),
  }));
  const [selectedSkuList, setSelectedSkuList] = useState<any>(
    defaultSelectedSkuList,
  );
  const [skuListViewData, setSkuListViewData] = useState<any>([]);
  const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);
  const [toastError, setToastError] = useState("");
  const [viewDetailsData, setViewDetailsData] = useState<ViewDetailType>({
    isViewDetailsOpen: isViewDetailsOpen,
    skuId: "",
    skuName: "",
    skuDescription: "",
    skuCommercialStatus: "",
    skuCost: 0,
    returnable: false,
    skuCostInterval: "",
    setIsViewDetailsOpen: setIsViewDetailsOpen,
    assetAdvice: assetAdvice || null,
    skuCostMetricCd: "",
    skuCostMacroCategoryCd: "",
    skuCostMetricNSR: 0,
    skuIndexation: 0,
    skuStatusName: "",
  });

  const isFixedCostSku = (sku: any) => {
    return sku.CostMetricCd === "fix_cost" || sku.CostIntervalCd === "onetime";
  };

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show]);

  useEffect(() => {
    if (productId !== undefined && show === true && isChangedActive === false) {
      dispatch(
        fetchSkuListData({
          search: "",
          productId: productId,
          page: 1,
          record: 1000,
          sortOrder: "ASC",
          sortColumn: "SkuId",
          exclude: "AutoTransact",
          include: "sku_retired",
        }),
      );
    }
    if (isChangedActive === true) {
      if (checkOutListState !== undefined) {
        const OrderSkuList = getSkuListFromOrderBundle(checkOutListState);
        const OrderUpdateobj = {
          Action: "updateOrder",
          OrderId: checkOutListState?.OrderId || 0,
          Description: checkOutListState?.Description || "",
          OrderSku: OrderSkuList,
        };
        const updateOrderBundleListAsync = async (
          orderUpdateObj: OrderUpdateObj,
        ) => {
          try {
            const response = await dispatch(
              updateOrderBundleList(orderUpdateObj),
            );
            if (response.meta.requestStatus === "fulfilled") {
              setIsChangedActive(false);
            } else {
              console.log("There is a issue in updating Order bundle");
              setIsChangedActive(false);
            }
          } catch (error) {
            console.error("Error updating order bundle:", error);
            setIsChangedActive(false);
          }
        };
        updateOrderBundleListAsync(OrderUpdateobj);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, productId, show, isChangedActive]);

  useEffect(() => {
    if (skuListData && skuListData !== null && show === true) {
      setSkuListViewData(
        skuListData.Result.map((sku: any) => {
          const selectedSku = selectedSkuList.filter(
            (selectedSku: any) => selectedSku.SkuId === sku.SkuId,
          );
          return {
            ...sku,
            Quantity:
              selectedSku.length > 0
                ? selectedSku[0].Quantity
                : sku.CostIntervalCd == "onetime"
                  ? 1
                  : "",
            QuantityError: "",
            IsSelected:
              selectedSku.length > 0 ? selectedSku[0].IsSelected : false,
            Duration:
              selectedSku.length > 0
                ? selectedSku[0].Duration
                : sku.CostIntervalCd == "onetime"
                  ? 1
                  : "",
            DurationError: "",
            TotalCost: 0,
            SkuRelation: sku?.SkuRelation?.map((relation: any) => {
              const selectedRelation =
                selectedSku.length > 0
                  ? selectedSku[0]?.SkuRelation.filter(
                      (relationSku: any) =>
                        relationSku.SkuId === relation.SkuId,
                    )
                  : [];
              return {
                ...relation,
                Quantity:
                  selectedRelation.length > 0
                    ? selectedRelation[0].Quantity
                    : relation.CostIntervalCd == "onetime"
                      ? 1
                      : "",
                QuantityError: "",
                Duration:
                  selectedRelation.length > 0
                    ? selectedRelation[0].Duration
                    : relation.CostIntervalCd == "onetime"
                      ? 1
                      : "",
                DurationError: "",
                TotalCost: 0,
              };
            }),
          };
        }),
      );
    }
  }, [skuListData, productId, show]);

  const handleViewDetails = (skuDetails: SkuDetailsType) => {
    setIsViewDetailsOpen(true);
    if (!viewDetailsData || viewDetailsData === null) {
      return;
    }
    setViewDetailsData({
      ...viewDetailsData,
      isViewDetailsOpen: true,
      skuId: skuDetails.SkuId,
      skuName: skuDetails.Name,
      skuDescription: skuDetails.Description,
      skuCommercialStatus: skuDetails.StatusCd,
      skuCost: skuDetails.CostMetricCost,
      returnable: skuDetails.IsReturnable,
      skuCostInterval: skuDetails.CostIntervalCd,
      skuCostMetricCd: skuDetails.CostMetricCd,
      skuCostMacroCategoryCd: skuDetails.CostMacroCategoryCd,
      skuCostMetricNSR: skuDetails.CostMetricNSR,
      skuIndexation: skuDetails.Indexation,
      skuStatusName: skuDetails.StatusName ? skuDetails.StatusName : "",
    });
  };

  const checkEmptyOrZero = (value: string | number) => {
    const numValue = Number(value);
    return value === 0 || isNaN(numValue);
  };

  const checkErrorExists = (error: string | undefined) => {
    return error === "" || error === undefined ? false : true;
  };

  const handleSave = async () => {
    let isError = false;
    for (const sku of selectedSkuList) {
      if (sku.IsSelected) {
        if (
          checkEmptyOrZero(sku.Quantity) ||
          checkEmptyOrZero(sku.Duration) ||
          Number(sku.Duration) < sku.CostIntervalMin ||
          checkErrorExists(sku.QuantityError) ||
          checkErrorExists(sku.DurationError)
        ) {
          isError = true;
          break;
        } else {
          isError = false;
        }
        for (const relation of sku.SkuRelation) {
          if (
            checkEmptyOrZero(sku.Quantity) ||
            checkEmptyOrZero(sku.Duration) ||
            Number(relation.Duration) < relation.CostIntervalMin ||
            checkErrorExists(relation.QuantityError) ||
            checkErrorExists(relation.DurationError)
          ) {
            isError = true;
            break;
          } else {
            isError = false;
          }
        }
      }
    }
    if (isError) {
      setToastError(
        "Please check the quantity and duration of the selected SKUs.",
      );
      setTimeout(() => {
        if (toastError !== "") {
          setToastError("");
        }
      }, 3000);
    } else {
      const OrderSkuList: {
        SkuId: any;
        ParentSkuId: any;
        Quantity: any;
        Intervals: any;
      }[] =
        unFilterSelectedSkuList.length > 0 ? [...unFilterSelectedSkuList] : [];
      selectedSkuList.forEach((sku: any) => {
        const OrderSku = {
          SkuId: sku.SkuId,
          ParentSkuId: "",
          Quantity: Number(sku.Quantity),
          Intervals: Number(sku.Duration),
        };
        OrderSkuList.push(OrderSku);
        if (sku?.SkuRelation?.length > 0) {
          sku?.SkuRelation?.forEach((relation: any) => {
            const OrderSkuRelation = {
              SkuId: relation.SkuId,
              ParentSkuId: sku.SkuId,
              Quantity: Number(relation.Quantity),
              Intervals: Number(relation.Duration),
            };
            OrderSkuList.push(OrderSkuRelation);
          });
        }
      });
      const OrderUpdateobj = {
        Action: "updateOrder",
        OrderId: checkOutListState?.OrderId || 0,
        Description: checkOutListState?.Description || "",
        OrderSku: OrderSkuList,
      };
      setIsLoading(true);
      const response = await dispatch(updateOrderBundleList(OrderUpdateobj));
      setIsLoading(false);
      if (response.meta.requestStatus === "fulfilled") {
        console.log("Order updated successfully");
        onSave("Order updated successfully");
        setShow(false);
        setShowEngagementException(false);
        dispatch(resetCheckoutSate());
        onOrderUpdated();
      } else {
        console.error("Failed to update order:", response.payload.message);
      }
    }
  };
  const handleSelectChange = (skuId: string) => {
    const updatedSkuListViewData = skuListViewData.map((sku: any) => {
      if (sku.SkuId === skuId && sku.IsSelected) {
        return {
          ...sku,
          IsSelected: !sku.IsSelected,
          Quantity: sku.CostIntervalCd == "onetime" ? 1 : "",
          QuantityError: "",
          Duration: sku.CostIntervalCd == "onetime" ? 1 : "",
          DurationError: "",
          SkuRelation: sku.SkuRelation.map((relation: any) => {
            return {
              ...relation,
              Quantity: relation.CostIntervalCd == "onetime" ? 1 : "",
              QuantityError: "",
              Duration: relation.CostIntervalCd == "onetime" ? 1 : "",
              DurationError: "",
            };
          }),
        };
      } else if (sku.SkuId === skuId && !sku.IsSelected) {
        return {
          ...sku,
          IsSelected: !sku.IsSelected,
          Quantity: 1,
          Duration:
            sku.CostIntervalMin && sku.CostIntervalMin != "0"
              ? sku.CostIntervalMin
              : "1",
          QuantityError: "",
          DurationError: "",
          SkuRelation: sku.SkuRelation.map((relation: any) => {
            return {
              ...relation,
              Quantity: 1,
              QuantityError: "",
              Duration:
                relation.CostIntervalMin && relation.CostIntervalMin != "0"
                  ? relation.CostIntervalMin
                  : "1",
              DurationError: "",
            };
          }),
        };
      }
      return sku;
    });

    setSkuListViewData(updatedSkuListViewData);

    const updatedSku = updatedSkuListViewData.filter(
      (sku: any) => sku.SkuId === skuId,
    );

    if (selectedSkuList.filter((sku: any) => sku.SkuId === skuId).length > 0) {
      const updatedSkuList = selectedSkuList.map((sku: any) => {
        if (sku.SkuId === skuId && sku.IsSelected) {
          return {
            ...sku,
            IsSelected: !sku.IsSelected,
            Quantity: sku.CostIntervalCd == "onetime" ? 1 : "",
            QuantityError: "",
            Duration: sku.CostIntervalCd == "onetime" ? 1 : "",
            DurationError: "",
            SkuRelation: sku.SkuRelation.map((relation: any) => {
              return {
                ...relation,
                Quantity: relation.CostIntervalCd == "onetime" ? 1 : "",
                QuantityError: "",
                Duration: relation.CostIntervalCd == "onetime" ? 1 : "",
                DurationError: "",
              };
            }),
          };
        } else if (sku.SkuId === skuId && !sku.IsSelected) {
          return {
            ...sku,
            IsSelected: !sku.IsSelected,
            Quantity: 1,
            Duration:
              sku.CostIntervalMin && sku.CostIntervalMin != "0"
                ? sku.CostIntervalMin
                : "1",
            QuantityError: "",
            DurationError: "",
            SkuRelation: sku.SkuRelation.map((relation: any) => {
              return {
                ...relation,
                Quantity: 1,
                QuantityError: "",
                Duration:
                  relation.CostIntervalMin && relation.CostIntervalMin != "0"
                    ? relation.CostIntervalMin
                    : "1",
                DurationError: "",
              };
            }),
          };
        }

        return sku;
      });
      setSelectedSkuList(updatedSkuList.filter((sku: any) => sku.IsSelected));
    } else {
      setSelectedSkuList([...selectedSkuList, updatedSku].flat());
    }
  };

  const handleQuantityChange = (skuId: string, quantity: string) => {
    const updatedSkuListViewData = skuListViewData.map((sku: any) => {
      if (sku.SkuId === skuId) {
        return {
          ...sku,
          Quantity: Number(quantity),
          QuantityError:
            quantity === ""
              ? "Required"
              : Number(quantity) <= 0
                ? "Min 1"
                : Number(quantity) > MAX_QUANTITY
                  ? `Max value is ${MAX_QUANTITY}`
                  : "",
        };
      }
      return sku;
    });
    setSkuListViewData(updatedSkuListViewData);

    const updatedSku = updatedSkuListViewData.filter(
      (sku: any) => sku.SkuId === skuId,
    );

    if (selectedSkuList.filter((sku: any) => sku.SkuId === skuId).length > 0) {
      const updatedSkuList = selectedSkuList.map((sku: any) => {
        if (sku.SkuId === skuId) {
          return {
            ...sku,
            Quantity: Number(quantity),
            QuantityError:
              quantity === ""
                ? "Required"
                : Number(quantity) <= 0
                  ? "Min 1"
                  : Number(quantity) > MAX_QUANTITY
                    ? `Max value is ${MAX_QUANTITY}`
                    : "",
          };
        }
        return sku;
      });
      setSelectedSkuList(updatedSkuList);
    } else {
      setSelectedSkuList([...selectedSkuList, updatedSku].flat());
    }
  };

  const handleRelationQuantityChange = (
    skuId: string,
    skuRelationId: string,
    quantity: string,
  ) => {
    const updatedSkuListViewData = skuListViewData.map((sku: any) => {
      if (sku.SkuId === skuId) {
        const updatedSkuRelation = sku?.SkuRelation?.map((relation: any) => {
          if (relation.SkuId === skuRelationId) {
            return {
              ...relation,
              Quantity: quantity,
              QuantityError:
                quantity === ""
                  ? "Required"
                  : Number(quantity) <= 0
                    ? "Min 1"
                    : Number(quantity) > MAX_QUANTITY
                      ? `Max value is ${MAX_QUANTITY}`
                      : "",
            };
          }
          return relation;
        });
        return {
          ...sku,
          SkuRelation: updatedSkuRelation,
        };
      }
      return sku;
    });
    setSkuListViewData(updatedSkuListViewData);

    const updatedSku = updatedSkuListViewData.filter(
      (sku: any) => sku.SkuId === skuId,
    );

    if (selectedSkuList.filter((sku: any) => sku.SkuId === skuId).length > 0) {
      const updatedSkuList = selectedSkuList.map((sku: any) => {
        if (sku.SkuId === skuId) {
          const updatedSkuRelation = sku?.SkuRelation?.map((relation: any) => {
            if (relation.SkuId === skuRelationId) {
              return {
                ...relation,
                Quantity: quantity,
                QuantityError:
                  quantity === ""
                    ? "Required"
                    : Number(quantity) <= 0
                      ? "Min 1"
                      : Number(quantity) > MAX_QUANTITY
                        ? `Max value is ${MAX_QUANTITY}`
                        : "",
              };
            }
            return relation;
          });
          return {
            ...sku,
            SkuRelation: updatedSkuRelation,
          };
        }
        return sku;
      });
      setSelectedSkuList(updatedSkuList);
    } else {
      setSelectedSkuList([...selectedSkuList, updatedSku].flat());
    }
  };

  const handleDurationChange = (skuId: string, duration: string) => {
    const updatedSkuListViewData = skuListViewData.map((sku: any) => {
      if (sku.SkuId === skuId) {
        const maxDuration =
          sku.CostIntervalCd === "month"
            ? MAX_DURATION_MONTHS
            : sku.CostIntervalCd === "week"
              ? MAX_DURATION_WEEKS
              : MAX_QUANTITY;
        return {
          ...sku,
          Duration: Number(duration),
          DurationError:
            duration == ""
              ? "Required"
              : Number(duration) < sku.CostIntervalMin
                ? "Min " +
                  sku.CostIntervalCd +
                  "s '" +
                  sku.CostIntervalMin +
                  "'"
                : Number(duration) > maxDuration
                  ? `Max value is ${maxDuration}`
                  : "",
        };
      }
      return sku;
    });
    setSkuListViewData(updatedSkuListViewData);

    const updatedSku = updatedSkuListViewData.filter(
      (sku: any) => sku.SkuId === skuId,
    );

    if (selectedSkuList.filter((sku: any) => sku.SkuId === skuId).length > 0) {
      const updatedSkuList = selectedSkuList.map((sku: any) => {
        if (sku.SkuId === skuId) {
          const maxDuration =
            sku.CostIntervalCd === "month"
              ? MAX_DURATION_MONTHS
              : sku.CostIntervalCd === "week"
                ? MAX_DURATION_WEEKS
                : MAX_QUANTITY;
          return {
            ...sku,
            Duration: duration,
            DurationError:
              duration == ""
                ? "Required"
                : Number(duration) < sku.CostIntervalMin
                  ? "Min " +
                    sku.CostIntervalCd +
                    " '" +
                    sku.CostIntervalMin +
                    "'"
                  : Number(duration) > maxDuration
                    ? `Max value is ${maxDuration}`
                    : "",
          };
        }
        return sku;
      });
      setSelectedSkuList(updatedSkuList);
    } else {
      setSelectedSkuList([...selectedSkuList, updatedSku].flat());
    }
  };

  const handleRelationDurationChange = (
    skuId: string,
    skuRelationId: string,
    duration: string,
  ) => {
    const updatedSkuListViewData = skuListViewData.map((sku: any) => {
      if (sku.SkuId === skuId) {
        const updatedSkuRelation = sku?.SkuRelation?.map((relation: any) => {
          const maxDuration =
            relation.CostIntervalCd === "month"
              ? MAX_DURATION_MONTHS
              : relation.CostIntervalCd === "week"
                ? MAX_DURATION_WEEKS
                : MAX_QUANTITY;
          if (relation.SkuId === skuRelationId) {
            return {
              ...relation,
              Duration: Number(duration),
              DurationError:
                duration == ""
                  ? "Required"
                  : Number(duration) < relation.CostIntervalMin
                    ? "Min " +
                      relation.CostIntervalCd +
                      " '" +
                      relation.CostIntervalMin +
                      "'"
                    : Number(duration) > maxDuration
                      ? `Max value is ${maxDuration}`
                      : "",
            };
          }
          return relation;
        });
        return { ...sku, SkuRelation: updatedSkuRelation };
      }
      return sku;
    });
    setSkuListViewData(updatedSkuListViewData);

    const updatedSku = updatedSkuListViewData.filter(
      (sku: any) => sku.SkuId === skuId,
    );

    if (selectedSkuList.filter((sku: any) => sku.SkuId === skuId).length > 0) {
      const updatedSkuList = selectedSkuList.map((sku: any) => {
        if (sku.SkuId === skuId) {
          const updatedSkuRelation = sku?.SkuRelation?.map((relation: any) => {
            const maxDuration =
              relation.CostIntervalCd === "month"
                ? MAX_DURATION_MONTHS
                : relation.CostIntervalCd === "week"
                  ? MAX_DURATION_WEEKS
                  : MAX_QUANTITY;
            if (relation.SkuId === skuRelationId) {
              return {
                ...relation,
                Duration: duration,
                DurationError:
                  duration == ""
                    ? "Required"
                    : Number(duration) < relation.CostIntervalMin
                      ? "Min " +
                        relation.CostIntervalCd +
                        " '" +
                        relation.CostIntervalMin +
                        "'"
                      : Number(duration) > maxDuration
                        ? `Max value is ${maxDuration}`
                        : "",
              };
            }
            return relation;
          });
          return { ...sku, SkuRelation: updatedSkuRelation };
        }
        return sku;
      });
      setSelectedSkuList(updatedSkuList);
    } else {
      setSelectedSkuList([...selectedSkuList, updatedSku].flat());
    }
  };

  const getCostInterval = (costInterval: string) => {
    if (costInterval == "month") {
      return "Per month";
    }
    if (costInterval == "onetime") {
      return "One time";
    }
    if (costInterval == "week") {
      return "Per week";
    }
    if (costInterval == "autotransact") {
      return "Auto Transact";
    }
  };

  const getDuration = (sku: any) => {
    if (sku.CostIntervalCd == "onetime") {
      return "Not Applicable";
    } else {
      return (
        <NumericInput
          id={sku.SkuId}
          value={sku.Duration}
          error={sku.DurationError}
          min={
            sku.CostIntervalMin && sku.CostIntervalMin != "0"
              ? Number(sku.CostIntervalMin)
              : 1
          }
          max={
            sku.CostIntervalCd === "month"
              ? MAX_DURATION_MONTHS
              : sku.CostIntervalCd === "week"
                ? MAX_DURATION_WEEKS
                : undefined
          }
          placeholder={sku.CostIntervalCd == "month" ? "Months" : "Weeks"}
          disabled={!sku.IsSelected}
          onChange={(val) => handleDurationChange(sku.SkuId, val)}
          label="duration"
        />
      );
    }
  };

  const getRelationDuration = (sku: any, skuRelation: any) => {
    if (skuRelation?.CostIntervalCd == "onetime") {
      return "Not Applicable";
    } else {
      return (
        <NumericInput
          id={skuRelation.SkuId}
          value={skuRelation.Duration}
          error={skuRelation.DurationError}
          min={
            skuRelation.CostIntervalMin && skuRelation.CostIntervalMin !== "0"
              ? Number(skuRelation.CostIntervalMin)
              : 1
          }
          max={
            skuRelation.CostIntervalCd === "month"
              ? MAX_DURATION_MONTHS
              : skuRelation.CostIntervalCd === "week"
                ? MAX_DURATION_WEEKS
                : undefined
          }
          placeholder={
            skuRelation.CostIntervalCd === "month" ? "Months" : "Weeks"
          }
          disabled={!sku.IsSelected}
          onChange={(val) =>
            handleRelationDurationChange(sku.SkuId, skuRelation.SkuId, val)
          }
          label="duration"
        />
      );
    }
  };

  const calculateTotalCost = (sku: any) => {
    if (!sku.IsSelected) {
      return "--";
    }
    const quantity = sku.Quantity || 0;
    const cost = sku.CostMetricCost || 0;
    const duration = sku.Duration || 0;

    const totalCost = quantity * cost * duration;
    return totalCost.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };

  const calculateRelationTotalCost = (sku: any, skuRelation: any) => {
    if (!sku.IsSelected) {
      return "--";
    }
    const quantity = skuRelation.Quantity || 0;
    const cost = skuRelation.CostMetricCost || 0;
    const duration = skuRelation.Duration || 0;
    const totalCost = quantity * cost * duration;

    return totalCost.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };

  const toastCloseHandler = () => {
    setToastError("");
  };

  const getOneTimeClass = (CostInterval: string) => {
    return CostInterval == "onetime" ? "sku_onetime" : "";
  };

  if ((isSkuListLoading && show) || isChangedActive) {
    return <Loader />;
  }

  return (
    <div className="update-order-modal-grid">
      <MotifTheme variant={"dark"}></MotifTheme>
      <MotifModal
        show={show}
        className="update-order-modal"
        onClose={() => {
          setShow(false);
        }}
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
      >
        <>
          <MotifModalHeader>{productName}</MotifModalHeader>
          {toastError !== "" && (
            <MotifToast variant="error" onClose={toastCloseHandler}>
              <div slot="toast-supporting-text">{toastError}</div>
            </MotifToast>
          )}
          <MotifModalBody>
            <div className="suggestion-text">
              From the list below, you can make changes within the SKUs selected
              for the product mentioned above
            </div>

            <section className="product-content-body">
              <div
                className={`sku-retired-error-message ${
                  retiredSkuList.length === 0 ? "hide" : ""
                }`}
              >
                <MotifIcon
                  src={IconoirWarningCircle}
                  className="warning-icon"
                />
                <div className="error-message">
                  {RETIRED_SKU_IN_CHECKOUT_MESSAGE}
                </div>
              </div>
              <div className="">
                <div className="sku-list">
                  {isSkuListLoading && <Loader />}
                  {errorSkuList && <div className="error">{errorSkuList}</div>}
                  {skuListData && skuListData.Result?.length >= 0 && (
                    <table className="motif-table-standard">
                      <thead>
                        <tr>
                          <th>Select</th>
                          <th className="sku-th">SKU Name/ SKU#</th>
                          <th>Quantity</th>
                          <th>Unit Cost</th>
                          <th>Duration</th>
                          <th>Total Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {skuListViewData.length > 0 &&
                          skuListViewData.map((sku: any) => {
                            let skuError = retiredSkuList.includes(sku?.SkuId)
                              ? true
                              : false;
                            if (sku?.SkuRelation?.length === 0) {
                              const sku_retired =
                                !sku.IsSelected &&
                                sku.StatusCd === "sku_retired"
                                  ? true
                                  : false;
                              if (sku_retired && !skuError) {
                                return;
                              }
                              return (
                                <tr key={sku.SkuId}>
                                  <td>
                                    <div className="checkbox-column">
                                      <CheckBoxToolTip
                                        isDisabled={
                                          sku.CostIntervalCd.toLowerCase() ===
                                          COST_INTERVAL_CODE.auto_transact
                                        }
                                        checked={sku.IsSelected}
                                        onChange={() =>
                                          handleSelectChange(sku.SkuId)
                                        }
                                        checkboxClassName="product-detail-checkbox"
                                      />
                                    </div>
                                  </td>
                                  <td
                                    className={`sku-cell ${
                                      skuError ? "sku-error" : ""
                                    }`}
                                  >
                                    <div className="sku-title">
                                      {skuError && (
                                        <MotifIcon
                                          src={IconoirWarningCircle}
                                          className="warning-icon"
                                        />
                                      )}
                                      <MotifTruncate
                                        maxLines={1}
                                        maxWidth={200}
                                      >
                                        {sku.Name}
                                      </MotifTruncate>
                                    </div>
                                    <div
                                      className="sku-number"
                                      style={{ display: "flex" }}
                                    >
                                      {sku.SkuId}
                                      <div
                                        onClick={() => handleViewDetails(sku)}
                                        className="view-details-link"
                                      >
                                        <div>View Details</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    {isFixedCostSku(sku) ? (
                                      <span>01</span>
                                    ) : (
                                      <NumericInput
                                        id={sku.SkuId}
                                        value={sku.Quantity}
                                        error={sku.QuantityError}
                                        min={0}
                                        max={MAX_QUANTITY}
                                        placeholder="Qty"
                                        disabled={!sku.IsSelected}
                                        onChange={(val) =>
                                          handleQuantityChange(sku.SkuId, val)
                                        }
                                        label="Quantity"
                                      />
                                    )}
                                  </td>
                                  <td>
                                    <div>
                                      {sku.CostMetricCost.toLocaleString(
                                        "en-US",
                                        {
                                          style: "currency",
                                          currency: "USD",
                                          maximumFractionDigits: 2,
                                          minimumFractionDigits: 2,
                                        },
                                      )}
                                    </div>
                                    <div className="suggested-table-content">
                                      {getCostInterval(sku.CostIntervalCd)}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="suggested-table-content">
                                      {getDuration(sku)}
                                    </div>
                                  </td>
                                  <td>{calculateTotalCost(sku)}</td>
                                </tr>
                              );
                            } else {
                              if (
                                !sku.IsSelected &&
                                sku.StatusCd === "sku_retired"
                              ) {
                                return;
                              }
                              return (
                                <tr key={sku.SkuId}>
                                  <td>
                                    <div className="checkbox-column">
                                      <MotifCheckbox
                                        id="checkbox-example"
                                        name="checkbox-example"
                                        onChange={() =>
                                          handleSelectChange(sku.SkuId)
                                        }
                                        checked={sku.IsSelected}
                                        disabled={
                                          !sku.IsSelected &&
                                          sku.StatusCd === "sku_retired"
                                        }
                                      >
                                        {""}
                                      </MotifCheckbox>
                                    </div>
                                  </td>
                                  <td
                                    className={`sku-cell ${
                                      skuError ? "sku-error" : ""
                                    }`}
                                  >
                                    <ul className="tree">
                                      <li className="tree-trigger">
                                        <div className="table-cell">
                                          <div>
                                            {skuError && (
                                              <MotifIcon
                                                src={IconoirWarningCircle}
                                                className="warning-icon"
                                              />
                                            )}
                                            <span className="sku-title">
                                              <MotifTruncate
                                                maxLines={1}
                                                maxWidth={200}
                                              >
                                                {sku.Name}
                                              </MotifTruncate>
                                            </span>
                                          </div>
                                          <div
                                            className="sku-number"
                                            style={{ display: "flex" }}
                                          >
                                            {sku.SkuId}{" "}
                                            <div
                                              onClick={() =>
                                                handleViewDetails(sku)
                                              }
                                              className="view-details-link"
                                            >
                                              <div>View Details</div>
                                            </div>
                                          </div>
                                        </div>
                                      </li>
                                      <ul>
                                        {sku?.SkuRelation?.map(
                                          (skuRelation: any) => {
                                            return (
                                              <li key={skuRelation.SkuId}>
                                                <div className="table-cell">
                                                  <div className="sku-title">
                                                    {skuError && (
                                                      <MotifIcon
                                                        src={
                                                          IconoirWarningCircle
                                                        }
                                                        className="warning-icon"
                                                      />
                                                    )}
                                                    <MotifTruncate
                                                      maxLines={1}
                                                      maxWidth={200}
                                                    >
                                                      {skuRelation.Name}
                                                    </MotifTruncate>
                                                  </div>
                                                  <div
                                                    className="sku-number"
                                                    style={{
                                                      display: "flex",
                                                    }}
                                                  >
                                                    {skuRelation.SkuId}{" "}
                                                    <div
                                                      onClick={() =>
                                                        handleViewDetails(
                                                          skuRelation,
                                                        )
                                                      }
                                                      className="view-details-link"
                                                    >
                                                      <div>View Details</div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </li>
                                            );
                                          },
                                        )}
                                      </ul>
                                    </ul>
                                  </td>
                                  <td className="qty-col">
                                    <div className="empty-cell"></div>
                                    <div
                                      className={`table-cell table-cell-child qty-child ${getOneTimeClass(
                                        sku.CostIntervalCd,
                                      )}`}
                                    >
                                      {isFixedCostSku(sku) ? (
                                        <div className="fix-qnt">01</div>
                                      ) : (
                                        <NumericInput
                                          id={sku.SkuId}
                                          value={sku.Quantity}
                                          error={sku.QuantityError}
                                          min={0}
                                          max={MAX_QUANTITY}
                                          placeholder="Qty"
                                          disabled={!sku.IsSelected}
                                          onChange={(val) =>
                                            handleQuantityChange(sku.SkuId, val)
                                          }
                                          label="Quantity"
                                        />
                                      )}
                                    </div>
                                    {sku?.SkuRelation?.map(
                                      (skuRelation: any) => {
                                        return (
                                          <div
                                            className={`table-cell table-cell-child qty-child ${getOneTimeClass(
                                              skuRelation.CostIntervalCd,
                                            )}`}
                                            key={skuRelation.SkuId}
                                          >
                                            {isFixedCostSku(skuRelation) ? (
                                              <div className="fix-qnt-child">
                                                01
                                              </div>
                                            ) : (
                                              <NumericInput
                                                id={skuRelation.SkuId}
                                                value={skuRelation.Quantity}
                                                error={
                                                  skuRelation.QuantityError
                                                }
                                                min={0}
                                                max={MAX_QUANTITY}
                                                placeholder="Qty"
                                                disabled={!sku.IsSelected}
                                                onChange={(val) =>
                                                  handleRelationQuantityChange(
                                                    sku.SkuId,
                                                    skuRelation.SkuId,
                                                    val,
                                                  )
                                                }
                                                label="Quantity"
                                              />
                                            )}
                                          </div>
                                        );
                                      },
                                    )}
                                  </td>
                                  <td className="qty-col">
                                    <div className="empty-cell"></div>
                                    <div
                                      className={`table-cell table-cell-child cost-interval-child ${getOneTimeClass(
                                        sku.CostIntervalCd,
                                      )}`}
                                    >
                                      <div>
                                        {sku.CostMetricCost.toLocaleString(
                                          "en-US",
                                          {
                                            style: "currency",
                                            currency: "USD",
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 2,
                                          },
                                        )}
                                      </div>
                                      <div className="suggested-table-content">
                                        {getCostInterval(sku.CostIntervalCd)}
                                      </div>
                                    </div>
                                    {sku?.SkuRelation?.map(
                                      (skuRelation: any) => {
                                        return (
                                          <div
                                            className={`table-cell table-cell-child cost-interval-child ${getOneTimeClass(
                                              skuRelation.CostIntervalCd,
                                            )}`}
                                            key={skuRelation.SkuId}
                                          >
                                            <div>
                                              {skuRelation.CostMetricCost.toLocaleString(
                                                "en-US",
                                                {
                                                  style: "currency",
                                                  currency: "USD",
                                                  maximumFractionDigits: 2,
                                                  minimumFractionDigits: 2,
                                                },
                                              )}
                                            </div>
                                            <div className="suggested-table-content">
                                              {getCostInterval(
                                                skuRelation.CostIntervalCd,
                                              )}
                                            </div>
                                          </div>
                                        );
                                      },
                                    )}
                                  </td>
                                  <td className="qty-col">
                                    <div className="empty-cell"></div>
                                    <div
                                      className={`table-cell table-cell-child duration-child suggested-table-content ${getOneTimeClass(
                                        sku.CostIntervalCd,
                                      )}`}
                                    >
                                      {getDuration(sku)}
                                    </div>
                                    {sku?.SkuRelation?.map(
                                      (skuRelation: any) => {
                                        return (
                                          <div
                                            className={`table-cell table-cell-child duration-child suggested-table-content ${getOneTimeClass(
                                              skuRelation.CostIntervalCd,
                                            )}`}
                                            key={skuRelation.SkuId}
                                          >
                                            {getRelationDuration(
                                              sku,
                                              skuRelation,
                                            )}
                                          </div>
                                        );
                                      },
                                    )}
                                  </td>
                                  <td qty-col>
                                    <div className="empty-cell-total"></div>
                                    <div
                                      className={`table-cell table-cell-child total-sku-child ${
                                        sku.CostIntervalCd == "onetime"
                                          ? "sku_onetime"
                                          : ""
                                      }`}
                                    >
                                      {calculateTotalCost(sku)}
                                      <div className="empty-cell-sku-total"></div>
                                    </div>
                                    {sku?.SkuRelation?.map(
                                      (skuRelation: any) => {
                                        return (
                                          <div
                                            className={`table-cell table-cell-child total-sku-child ${getOneTimeClass(
                                              skuRelation.CostIntervalCd,
                                            )}`}
                                            key={skuRelation.SkuId}
                                          >
                                            {calculateRelationTotalCost(
                                              sku,
                                              skuRelation,
                                            )}
                                            <div className="empty-cell-sku-total"></div>
                                          </div>
                                        );
                                      },
                                    )}
                                  </td>
                                </tr>
                              );
                            }
                          })}
                        {skuListViewData.length == 0 && (
                          <tr>
                            <td colSpan={6} className="no-data">
                              No data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </section>
          </MotifModalBody>
          <MotifModalFooter>
            <MotifButton
              variant={"secondary"}
              onClick={() => {
                setShow(false);
              }}
            >
              Cancel
            </MotifButton>
            <MotifButton
              variant={"primary"}
              onClick={handleSave}
              loading={isLoading ? true : false}
            >
              {"Save Changes"}
            </MotifButton>
          </MotifModalFooter>
        </>
      </MotifModal>
      {isViewDetailsOpen && <ViewDetails viewDetailsData={viewDetailsData} />}
    </div>
  );
}
